import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { useFocusTrap } from '../../navigation/hooks/useFocusTrap';
import { SMLEmptyState } from '../SMLEmptyState/SMLEmptyState';
import { usePaletteEntries } from './usePaletteEntries';
import { usePaletteNavigate } from './usePaletteNavigate';
import { NoResultWish } from '../../wishBox/NoResultWish';
import { PaletteResultRow } from './PaletteResultRow';
import { PalettePageRow } from './PalettePageRow';
import { PaletteSeeAllRow } from './PaletteSeeAllRow';
import { PaletteFooter } from './PaletteFooter';
import { paletteSectionLabels } from './paletteSectionLabels';
import { commandPaletteMessages as messages } from './commandPaletteMessages';
import styles from './CommandPalette.module.scss';

interface PaletteDialogProps {
  onClose: () => void;
}

/**
 * The open palette: the text field, the artists, tracks and playlists, "Pages and actions" (menu
 * items, tabs, Settings tabs, by old names too) and "See all results with filters in Search"
 * (Shift+Enter). Arrow keys move the selection, Enter opens it, Escape closes. Tab stays in the dialog.
 */
const PaletteDialogComponent: React.FC<PaletteDialogProps> = ({ onClose }) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { sections, pages, isSearching, hasText, entries } = usePaletteEntries(query);
  const go = usePaletteNavigate(query, onClose);

  // The dialog holds the keyboard: the focus goes to the text field, Tab stays in the dialog,
  // Escape closes it, and on close the focus goes back to the control that opened it.
  useFocusTrap(paletteRef, true, onClose);

  // New results come in above the pages: the selection goes back to the first row.
  useEffect(() => {
    setSelectedIndex(0);
  }, [sections]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(0, Math.min(prev + 1, entries.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const entry = e.shiftKey && hasText ? entries[entries.length - 1] : entries[selectedIndex];
        if (entry !== undefined) {
          go(entry);
        }
      }
    },
    [entries, go, hasText, selectedIndex]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const rowProps = (index: number) => ({
    selected: index === selectedIndex,
    onPick: () => {
      const entry = entries[index];
      if (entry !== undefined) {
        go(entry);
      }
    },
    onHover: () => setSelectedIndex(index),
  });

  // The "See all results" row is the last entry (usePaletteEntries).
  const seeAllRow = rowProps(entries.length - 1);
  let index = 0;
  const nothingFound = hasText && !isSearching && sections.length === 0 && pages.length === 0;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.overlay} onClick={handleBackdropClick} onKeyDown={handleKeyDown}>
      <div
        ref={paletteRef}
        className={styles.palette}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage(messages.placeholder)}
      >
        <div className={styles.inputContainer}>
          <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder={intl.formatMessage(messages.placeholder)}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
          />
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={intl.formatMessage(messages.close)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={styles.results}>
          {sections.map((section) => (
            <div key={section.type}>
              <div className={styles.sectionLabel}>{intl.formatMessage(paletteSectionLabels[section.type])}</div>
              {section.results.map((result) => {
                const row = rowProps(index);
                index += 1;
                return (
                  <PaletteResultRow
                    key={result.searchItem.id}
                    result={result}
                    selected={row.selected}
                    onPick={row.onPick}
                    onHover={row.onHover}
                  />
                );
              })}
            </div>
          ))}
          {isSearching && <div className={styles.emptyState}>{intl.formatMessage(messages.searching)}</div>}
          {pages.length > 0 && (
            <div>
              <div className={styles.sectionLabel}>{intl.formatMessage(messages.sectionPages)}</div>
              {pages.map((match) => {
                const row = rowProps(index);
                index += 1;
                return (
                  <PalettePageRow
                    key={match.page.id}
                    match={match}
                    selected={row.selected}
                    onPick={row.onPick}
                    onHover={row.onHover}
                  />
                );
              })}
            </div>
          )}
          {nothingFound && (
            <SMLEmptyState
              icon={faMagnifyingGlass}
              title={intl.formatMessage(messages.noResults)}
              description={intl.formatMessage(messages.noResultsDescription)}
            />
          )}
          {nothingFound && (
            <div className="text-center">
              <NoResultWish surface="commandPalette" kind="all" query={query} onOpen={onClose} />
            </div>
          )}
          {hasText && (
            <PaletteSeeAllRow selected={seeAllRow.selected} onPick={seeAllRow.onPick} onHover={seeAllRow.onHover} />
          )}
        </div>

        <PaletteFooter />
      </div>
    </div>
  );
};

export const PaletteDialog = TypedMemo(PaletteDialogComponent);
